
/**
 * Hook para gerenciar apelidos de anúncios
 * 
 * O que faz:
 * - Busca apelidos existentes para os anúncios (da clínica ativa ou selecionada pelo admin)
 * - Permite criar novos apelidos
 * - Permite atualizar apelidos existentes
 * - CORRIGIDO: Funciona tanto para usuários normais quanto administradores
 * 
 * Onde é usado:
 * - Componente AdPerformanceCard para edição inline de nomes
 * - Dashboard e LeadsListModal para exibição de apelidos
 * 
 * Como se conecta:
 * - Interage com a tabela 'ad_aliases' do Supabase
 * - Utiliza RLS para segurança por clínica
 * - NOVO: Aceita targetClinicaId para modo admin
 */

import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useClinica } from '@/contexts/ClinicaContext';

export interface AdAlias {
  id: string;
  clinica_id: string;
  ad_name_original: string;
  ad_alias: string;
  created_at: string;
  updated_at: string;
}

const normalizarNomeAnuncio = (nome: string): string =>
  nome.trim().toLowerCase().replace(/\s+/g, ' ');

// CORRIGIDO: Hook agora aceita targetClinicaId para modo admin
export const useAdAliases = (targetClinicaId?: string) => {
  const { clinicaId: userClinicaId } = useClinica();
  const queryClient = useQueryClient();

  // CORRIGIDO: Usar targetClinicaId quando fornecido (modo admin) ou clinicaId do usuário
  const effectiveClinicaId = targetClinicaId || userClinicaId;

  // Buscar todos os apelidos da clínica efetiva
  const { data: aliases = [], isLoading, isError } = useQuery({
    queryKey: ['ad-aliases', effectiveClinicaId],
    queryFn: async () => {
      if (!effectiveClinicaId) return [];
      
      // SELECT em ad_aliases: todos os apelidos da clínica efetiva para exibição e edição.
      // Filtro: clinica_id; Risco: RLS mantém o isolamento entre clínicas e admins autorizados.
      const { data, error } = await supabase
        .from('ad_aliases')
        .select('*')
        .eq('clinica_id', effectiveClinicaId);

      if (error) {
        console.error('Erro ao buscar apelidos de anúncios:', error);
        throw error;
      }

      return data as AdAlias[];
    },
    enabled: !!effectiveClinicaId,
  });

  // Indexa os apelidos uma vez por clínica para que ranking, busca e ordenação não façam
  // um `.find` linear para cada anúncio a cada render do modal.
  const aliasIndex = useMemo(() => {
    const exatos = new Map<string, string>();
    const normalizados = new Map<string, string>();

    aliases.forEach((alias) => {
      if (!alias.ad_name_original || !alias.ad_alias) return;
      if (!exatos.has(alias.ad_name_original)) exatos.set(alias.ad_name_original, alias.ad_alias);
      const nomeNormalizado = normalizarNomeAnuncio(alias.ad_name_original);
      if (!normalizados.has(nomeNormalizado)) normalizados.set(nomeNormalizado, alias.ad_alias);
    });

    return { exatos, normalizados };
  }, [aliases]);

  // Mantém a função estável para que `useMemo` dos rankings não recalcule sem mudança real.
  const getAliasForAd = useCallback((adNameOriginal: string): string | null => {
    if (!adNameOriginal || !adNameOriginal.trim()) {
      return null;
    }

    return aliasIndex.exatos.get(adNameOriginal)
      ?? aliasIndex.normalizados.get(normalizarNomeAnuncio(adNameOriginal))
      ?? null;
  }, [aliasIndex]);

  // CORRIGIDO: Mutation para criar ou atualizar apelido
  const saveAliasMutation = useMutation({
    mutationFn: async ({ adNameOriginal, alias }: { adNameOriginal: string; alias: string }) => {
      if (!effectiveClinicaId) throw new Error('ID da clínica não encontrado');

      // Verificar se já existe um apelido para este anúncio (busca normalizada)
      const nomeNormalizado = normalizarNomeAnuncio(adNameOriginal);
      const existingAlias = aliases.find(a => 
        a.ad_name_original === adNameOriginal || 
        normalizarNomeAnuncio(a.ad_name_original) === nomeNormalizado
      );

      if (existingAlias) {
        // Atualizar apelido existente
        // UPDATE em ad_aliases: altera somente o texto do apelido e mantém o vínculo da clínica.
        const { data, error } = await supabase
          .from('ad_aliases')
          .update({ 
            ad_alias: alias,
            ad_name_original: adNameOriginal // Atualizar também o nome original para a versão mais recente
          })
          .eq('id', existingAlias.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Criar novo apelido
        // INSERT em ad_aliases: cria o apelido no escopo da clínica efetiva.
        const { data, error } = await supabase
          .from('ad_aliases')
          .insert({
            clinica_id: effectiveClinicaId,
            ad_name_original: adNameOriginal,
            ad_alias: alias
          })
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      // Recarregar a lista de apelidos
      queryClient.invalidateQueries({ queryKey: ['ad-aliases', effectiveClinicaId] });
      // Alias participa da busca/ordenação no servidor; atualizar só ranking/resumo desta clínica.
      queryClient.invalidateQueries({ predicate: consulta => consulta.queryKey[0] === 'dashboard-v1'
        && consulta.queryKey[2] === effectiveClinicaId && ['anuncios', 'resumo'].includes(String(consulta.queryKey[4])) });
    },
    onError: (error) => {
      console.error('Erro ao salvar apelido do anúncio:', error);
    }
  });

  // CORRIGIDO: Mutation para deletar apelido
  const deleteAliasMutation = useMutation({
    mutationFn: async (adNameOriginal: string) => {
      const nomeNormalizado = normalizarNomeAnuncio(adNameOriginal);
      const existingAlias = aliases.find(a => 
        a.ad_name_original === adNameOriginal || 
        normalizarNomeAnuncio(a.ad_name_original) === nomeNormalizado
      );
      
      if (!existingAlias) throw new Error('Apelido não encontrado');

      // DELETE em ad_aliases: remove somente o registro identificado pela chave autorizada.
      const { error } = await supabase
        .from('ad_aliases')
        .delete()
        .eq('id', existingAlias.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ad-aliases', effectiveClinicaId] });
      // Remoção de alias deve refletir também na pesquisa e na prévia, sem invalidar atendimento.
      queryClient.invalidateQueries({ predicate: consulta => consulta.queryKey[0] === 'dashboard-v1'
        && consulta.queryKey[2] === effectiveClinicaId && ['anuncios', 'resumo'].includes(String(consulta.queryKey[4])) });
    },
    onError: (error) => {
      console.error('Erro ao deletar apelido do anúncio:', error);
    }
  });

  return {
    aliases,
    isLoading,
    isError,
    getAliasForAd,
    saveAlias: saveAliasMutation.mutateAsync,
    deleteAlias: deleteAliasMutation.mutateAsync,
    isSaving: saveAliasMutation.isPending,
    isDeleting: deleteAliasMutation.isPending,
    // A clínica efetiva também orienta o escopo de mutações no modo administrativo.
    effectiveClinicaId
  };
};
